import type { CardId, DealResult, Seat, Suit, TrickPlay } from '@ubs/rules';
import type { GameEvent, ViewMessage } from '@ubs/protocol';

/**
 * EKRANDA GÖRÜNEN durum — yetkili durumun (`ViewMessage`) birkaç yüz ms
 * gerisinden gelen kopyası.
 *
 * Neden ayrı: sunucu "şu an durum bu" diyor, ama oyuncunun görmesi gereken
 * şey "ne oldu". Kartlar masaya inerken, tur toplanırken, koz duyurulurken
 * ekran o anın içinde kalmalı. Olaylar bu duruma sırayla uygulanıyor; kuyruk
 * boşalınca `reconcile` ile yetkili duruma oturuluyor, yani ikisi asla kalıcı
 * olarak ayrışamıyor.
 */
export interface DisplayState {
  hand: CardId[];
  /** Masadaki turun kartları, oynanma sırasıyla. */
  pile: TrickPlay[];
  /** Koltuk başına elde kalan kart sayısı. */
  counts: number[];
  /** Koltuk başına alınan el sayısı. */
  tricks: number[];
  trump: Suit | null;
  /** Turu alan koltuk — tur toplanmadan önce kısa süre gösterilen rozet. */
  trickWinner: Seat | null;
  /** El sonu özeti; yalnızca `dealEnd` olayı oynarken dolu. */
  dealSummary: DealResult | null;
  /** Koz duyurusu; yalnızca `trumpChosen` olayı oynarken dolu. */
  trumpBanner: { seat: Seat; suit: Suit } | null;
  /** Alışverişte uçan kartların yönü; yalnızca `exchangeStep` oynarken dolu. */
  exchangeFlight: { from: Seat; to: Seat; count: number } | null;
}

export const initialDisplay = (): DisplayState => ({
  hand: [],
  pile: [],
  counts: [0, 0, 0],
  tricks: [0, 0, 0],
  trump: null,
  trickWinner: null,
  dealSummary: null,
  trumpBanner: null,
  exchangeFlight: null,
});

export function applyEvent(state: DisplayState, event: GameEvent, me: Seat): DisplayState {
  switch (event.kind) {
    case 'play': {
      /**
       * Tur BURADA toplanıyor, `trickEnd`'de değil.
       *
       * `trickEnd` geldiğinde kartları silseydik, "kim aldı" rozetinin
       * göründüğü süre boyunca masa boş kalırdı. Üç kart masada dururken
       * rozet çıkıyor, sıradaki kart atılınca yığın yenileniyor.
       */
      const closing = state.pile.length >= 3;
      return {
        ...state,
        pile: [...(closing ? [] : state.pile), { seat: event.seat, card: event.card }],
        trickWinner: closing ? null : state.trickWinner,
        counts: state.counts.map((n, s) => (s === event.seat ? Math.max(0, n - 1) : n)),
        hand: event.seat === me ? state.hand.filter((c) => c !== event.card) : state.hand,
      };
    }

    case 'trickEnd':
      return { ...state, trickWinner: event.winner, tricks: [...event.tricks] };

    case 'dealStart':
      return {
        ...initialDisplay(),
        counts: [16, 16, 16],
        // El bitince elim zaten boşalmış oluyor; yeni el `reconcile` ile geliyor.
        hand: [],
      };

    case 'trumpChosen':
      return { ...state, trump: event.suit, trumpBanner: { seat: event.seat, suit: event.suit } };

    case 'buryDone':
      return { ...state, trumpBanner: null };

    case 'exchangeStart':
      return { ...state, exchangeFlight: null };

    case 'exchangeStep':
      return {
        ...state,
        exchangeFlight: { from: event.from, to: event.to, count: event.count },
      };

    case 'exchangeDone':
      return { ...state, exchangeFlight: null };

    case 'dealEnd':
      return { ...state, dealSummary: event.result, pile: [], trickWinner: null };

    default:
      return state;
  }
}

/** Kuyruk boşaldı: yetkili duruma otur. */
export function reconcile(state: DisplayState, view: ViewMessage): DisplayState {
  return {
    ...state,
    hand: [...view.hand],
    pile: view.trick.plays.map((p) => ({ ...p })),
    counts: [...view.handCounts],
    tricks: [...view.tricks],
    trump: view.trump,
    // Geçici perdeler burada kapanıyor: hepsi kendi olayının süresi kadar
    // görünüp yetkili duruma oturulduğunda sona eriyor.
    trickWinner: null,
    dealSummary: null,
    trumpBanner: null,
    exchangeFlight: null,
  };
}
